import { useEffect } from "react";

const useLenis = () => {
    useEffect(() => {
        // Only run on client side
        if (typeof window === "undefined") return;

        // Dynamic import to avoid SSR issues
        import("lenis").then(({ default: Lenis }) => {
            
            const lenis = new Lenis({
                lerp: 0.1,
                // Prevent scroll conflicts with TOC and horizontal scrolling elements
                prevent: (node: HTMLElement) => {
                    // Prevent Lenis from handling scroll on TOC elements and horizontal scroll containers
                    return node.classList.contains('toc-body') || 
                           node.closest('.toc-card-wrapper') !== null ||
                           node.closest('.table-responsive-wrapper') !== null;
                },
                // Optimize for performance
                duration: 1.2,
                easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
                // Reduce forced reflows by batching updates
                syncTouch: false,
                touchMultiplier: 2,
                // Optimize scroll performance
                infinite: false,
                gestureOrientation: 'vertical'
            });
            
            // Expose Lenis instance globally for use in components
            (window as any).lenis = lenis;
            
            // Optimized RAF loop to prevent excessive calls
            let rafId: number;
            const raf = (time: number) => {
                lenis.raf(time);
                rafId = requestAnimationFrame(raf);
            };

            rafId = requestAnimationFrame(raf);

            // Intercept link clicks to prevent scroll behavior during navigation
            const handleLinkClick = (e: MouseEvent) => {
                const target = e.target as HTMLElement;
                const link = target.closest('a[href]') as HTMLAnchorElement;
                
                if (link && link.href) {
                    try {
                        const url = new URL(link.href);
                        const isInternalLink = url.origin === window.location.origin;
                        const isAnchorLink = link.hash && link.pathname === window.location.pathname;
                        
                        // Skip anchor links (same page navigation)
                        if (isAnchorLink) {
                            return;
                        }
                        
                        // For internal navigation links, stop any ongoing scroll animation
                        // without causing any visible scroll movement on current page
                        if (isInternalLink && lenis) {
                            // Freeze Lenis at current position to stop animation
                            // This prevents scroll position from being preserved during navigation
                            try {
                                const currentScroll = window.scrollY || window.pageYOffset;
                                // Set scroll to current position immediately to stop animation
                                lenis.scrollTo(currentScroll, { immediate: true });
                            } catch {
                                // Silently fail if method doesn't exist
                            }
                        }
                    } catch {
                        // Invalid URL, skip handling
                    }
                }
            };

            // Add click listener with capture phase to intercept early
            document.addEventListener('click', handleLinkClick, true);

            return () => {
                // Remove event listener
                document.removeEventListener('click', handleLinkClick, true);
                
                // Cancel RAF loop to prevent memory leaks
                if (rafId) {
                    cancelAnimationFrame(rafId);
                }
                lenis.destroy();
                delete (window as any).lenis;
            };
        }).catch(console.error);
    }, []);
};

export default useLenis;
